import { $fetch } from "ofetch";
import { appendSupportRef, serverSupportRef } from "./supportRef";

const TOKEN_KEY = "ledger.auth.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

/**
 * Structured error payload some backend handlers return — surfaces in the UI
 * with deep links to the page where the user can fix the problem (e.g.
 * Setup → Integrations) plus an optional external link (e.g. Anthropic
 * billing).
 */
export type ApiErrorDetail = {
  /** Short user-facing message. */
  detail: string;
  /** In-app deep link, e.g. {label:"Open Setup → Integrations", href:"/settings/integrations"} */
  action?: { label: string; href: string };
  /** External link, e.g. {label:"Open Anthropic billing", href:"https://..."} */
  external?: { label: string; href: string };
  /** Machine-readable reason code, e.g. "anthropic_billing", "anthropic_auth". */
  reason?: string;
};

/**
 * Extract a user-facing message from an error thrown by `api()`.
 *
 * ofetch wraps non-2xx responses in a FetchError with the parsed JSON body
 * on `.data`. FastAPI puts the human-readable reason on `data.detail` —
 * this helper pulls that out and falls back to the generic error message
 * (e.g. "[POST] '/api/foo': 502 Bad Gateway") only when no detail exists.
 */
export function errorMessage(err: unknown): string {
  // A server failure (5xx) names its support reference; keep it on whatever
  // the user is shown so support can find the Sentry event (FIX-LOG 93).
  return appendSupportRef(baseErrorMessage(err), serverSupportRef(err));
}

function baseErrorMessage(err: unknown): string {
  const struct = errorDetail(err);
  if (struct) return struct.detail;
  if (!err) return "Unknown error";
  const e = err as { data?: { detail?: unknown }; message?: string };
  const detail = e?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  if (Array.isArray(detail) && detail.length) {
    // FastAPI validation errors come back as a list of {msg, loc, ...}
    return detail
      .map((d: { msg?: string } | string) =>
        typeof d === "string" ? d : d?.msg || JSON.stringify(d),
      )
      .join("; ");
  }
  // Domain errors (ConflictError / ValidationError / NotFoundError) are
  // serialised by the app's exception handlers as {error: {code, message}} —
  // a different envelope from FastAPI's `detail`. Without this branch every
  // one of them surfaced as a bare "409 Conflict", throwing away the message
  // that tells the user what to actually do about it.
  const enveloped = (err as { data?: { error?: { message?: unknown } } })?.data?.error;
  if (typeof enveloped?.message === "string" && enveloped.message.trim()) {
    return enveloped.message;
  }
  return e?.message || "Something went wrong.";
}

/**
 * Returns the structured error payload (with action/external links) when
 * the backend supplied one — otherwise null. Use this when you want to
 * render action buttons next to the error message.
 */
export function errorDetail(err: unknown): ApiErrorDetail | null {
  if (!err) return null;
  const e = err as { data?: { detail?: unknown } };
  const d = e?.data?.detail;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    const obj = d as Record<string, unknown>;
    if (typeof obj.detail === "string") {
      return {
        detail: obj.detail,
        action: obj.action as ApiErrorDetail["action"],
        external: obj.external as ApiErrorDetail["external"],
        reason: typeof obj.reason === "string" ? obj.reason : undefined,
      };
    }
  }
  return null;
}

export const api = $fetch.create({
  // Same-origin. In dev Vite proxies known API prefixes to :8000; in prod
  // FastAPI serves both the SPA + API on one port.
  baseURL: "",
  onRequest({ options }) {
    const token = getToken();
    if (token) {
      const headers = new Headers(options.headers as HeadersInit | undefined);
      headers.set("Authorization", `Bearer ${token}`);
      options.headers = headers;
    }
  },
  onResponseError({ response }) {
    if (response.status === 401) {
      setToken(null);
      if (window.location.pathname !== "/login") {
        window.location.assign("/login");
      }
    }
  },
});

/**
 * Download an authenticated binary response (PDF, XLSX, …) and save it.
 *
 * Use this for every file download instead of a bare `fetch(...).then(r =>
 * r.blob())`. Hand-rolled versions kept reading the wrong localStorage key and
 * never checked the status, so an auth failure was saved verbatim as the
 * "downloaded" file — a 30-byte JSON error named `.pdf`, which every viewer
 * reports as a corrupt document.
 *
 * Throws on a non-2xx response so the caller can surface a real message.
 */
export async function downloadFile(url: string, filename: string): Promise<void> {
  const token = getToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    if (res.status === 401) {
      setToken(null);
      if (window.location.pathname !== "/login") window.location.assign("/login");
    }
    // Body is an error payload, not the file — surface it rather than saving it.
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.clone().json();
      if (body?.detail) detail = typeof body.detail === "string" ? body.detail : detail;
    } catch {
      /* not JSON — keep the status line */
    }
    throw new Error(`Download failed: ${detail}`);
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}
